import React, { useState } from 'react';
import AgentNumber from '../../../features/onboarding/AgentNumber';
import type { TestPage } from '../../../features/onboarding/onboardingChecklist';
import {
  useConfirmTested,
  useSendTestFeedback,
  useTestPage,
} from '../../../features/onboarding/useOnboardingChecklist';
import {
  BlockSkeleton,
  Button,
  Callout,
  Field,
  IconCheckCircle,
  Reason,
  Textarea,
} from '../../../ui/v2';
import { AgentVoiceBlock } from '../../StoreInfo/v2/VoiceSection';
import { StepIntro, StepPart } from './StepFlow';

/** Sounds right, or feedback for the store's Ask Arthur rep. */
const Verdict: React.FC<{ page: TestPage }> = ({ page }) => {
  const confirm = useConfirmTested();
  const feedback = useSendTestFeedback();
  const [writing, setWriting] = useState(false);
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const latestCall = page.calls[0]?.call_id;

  if (page.test_status === 'done') {
    return (
      <Callout
        tone="accent"
        icon={<IconCheckCircle size={14} />}
        title="Marked as tested."
      />
    );
  }
  if (!page.can_confirm) {
    return (
      <Reason>
        Your store’s owner or a manager marks the agent as tested.
      </Reason>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      {(confirm.error || feedback.error) && (
        <Callout tone="danger">
          {((confirm.error || feedback.error) as Error).message}
        </Callout>
      )}
      {sent && !writing && (
        <Callout tone="accent" title="Sent to your Ask Arthur rep." />
      )}
      {writing ? (
        <form
          className="flex max-w-[560px] flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            feedback.mutate(
              { message, call_id: latestCall },
              {
                onSuccess: () => {
                  setSent(true);
                  setWriting(false);
                  setMessage('');
                },
              },
            );
          }}
        >
          <Field label="Feedback for your Ask Arthur rep">
            {(id) => (
              <Textarea
                id={id}
                rows={3}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
              />
            )}
          </Field>
          <div className="flex gap-2">
            <Button
              type="submit"
              variant="primary"
              loading={feedback.isPending}
              disabledReason={
                message.trim() ? undefined : 'Write your feedback first'
              }
            >
              Send
            </Button>
            <Button variant="ghost" onClick={() => setWriting(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              loading={confirm.isPending}
              disabledReason={
                page.calls.length ? undefined : 'Call your agent first'
              }
              onClick={() => confirm.mutate()}
            >
              Sounds right
            </Button>
            <Button onClick={() => setWriting(true)}>Send feedback</Button>
          </div>
          {!page.calls.length && (
            <Reason>Call your agent first, then say how it went.</Reason>
          )}
        </div>
      )}
    </div>
  );
};

const TestBody: React.FC<{ page: TestPage }> = ({ page }) => {
  if (!page.agent_number) {
    return (
      <Callout tone="warning" title="We’re setting up your test number">
        We’ll let you know as soon as it’s ready.
      </Callout>
    );
  }
  return (
    <>
      <StepPart>
        <StepIntro>
          Call your agent to test it. Any feedback goes to your Ask Arthur rep.
        </StepIntro>
        <AgentNumber number={page.agent_number} />
      </StepPart>

      <StepPart aria-label="Voice">
        <AgentVoiceBlock />
      </StepPart>

      <StepPart aria-label="Verdict">
        <Verdict page={page} />
      </StepPart>
    </>
  );
};

/** Test your agent, inside its Getting started step: call it, pick a voice,
 * then say it sounds right or send feedback. */
export default function TestStep() {
  const test = useTestPage();

  if (test.isPending) return <BlockSkeleton rows={4} />;
  if (test.isError || !test.data) {
    return (
      <Callout tone="danger">
        We couldn’t load your test number. Refresh the page to try again.
      </Callout>
    );
  }
  return <TestBody page={test.data} />;
}
